import React, { useState } from 'react';
import { usePartnerList } from '@/hooks/queries/partner/useList';
import { getMediaUrl } from '@/utils/common';
import { usePartnerCreate, usePartnerUpdate, usePartnerDelete } from '@/hooks/mutations/partner';
import { Partner, PartnerInput } from '@/types/partners';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Search, Edit2, Trash2, Handshake, ExternalLink } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { Pagination } from '@/components/Pagination';

const PartnersPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  const [formData, setFormData] = useState<PartnerInput>({
    logo: '',
    website: '',
    translations: [
      { locale: 'uz', name: '', description: '' },
      { locale: 'ru', name: '', description: '' },
      { locale: 'en', name: '', description: '' },
    ],
  });

  const { data: partnersData, isLoading, refetch } = usePartnerList({ search, pageNumber: page, pageSize: 20 });
  const { mutate: createPartner, isPending: isCreating } = usePartnerCreate();
  const { mutate: updatePartner, isPending: isUpdating } = usePartnerUpdate();
  const { mutate: deletePartner } = usePartnerDelete();

  const handleOpenCreate = () => {
    setSelectedPartner(null);
    setFormData({
      logo: '',
      website: '',
      translations: [
        { locale: 'uz', name: '', description: '' },
        { locale: 'ru', name: '', description: '' },
        { locale: 'en', name: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (partner: Partner) => {
    setSelectedPartner(partner);
    setFormData({
      logo: partner.logo || '',
      website: partner.website || '',
      translations: [
        { locale: 'uz', name: partner.name || '', description: partner.description || partner.tavsif || '' },
        { locale: 'ru', name: '', description: '' },
        { locale: 'en', name: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'name' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPartner) {
      updatePartner(
        { id: selectedPartner.id, data: formData },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Hamkor yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Hamkor yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createPartner(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi hamkor yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Hamkor yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedPartner) return;
    deletePartner(selectedPartner.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Hamkor o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedPartner(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Hamkor o'chirilmadi", variant: 'destructive' }),
    });
  };

  const partnersList: Partner[] = partnersData?.data?.data || partnersData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Hamkorlar</h1>
          <p className="text-sm text-slate-500 mt-1">Platforma bilan hamkorlik qilayotgan kompaniya va tashkilotlar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi hamkor
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3.5 top-3 size-4 text-slate-400" />
            <Input
              placeholder="Hamkor nomi bo'yicha qidirish..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 h-10 bg-slate-50/50 border-slate-200 rounded-xl"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : partnersList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Handshake className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha hamkorlar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Logo</th>
                  <th className="px-4 py-3.5">Nomi</th>
                  <th className="px-4 py-3.5">Veb-sayt</th>
                  <th className="px-4 py-3.5">Tavsif</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {partnersList.map((partner) => (
                  <tr key={partner.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {partner.logo ? (
                        <img src={getMediaUrl(partner.logo)} alt={partner.name} className="h-9 w-auto max-w-[80px] object-contain rounded border border-slate-100 p-1" />
                      ) : (
                        <div className="size-9 rounded bg-slate-100 text-slate-400 flex items-center justify-center font-bold">
                          H
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{partner.name || '—'}</td>
                    <td className="px-4 py-3">
                      {partner.website ? (
                        <a href={partner.website} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                          {partner.website} <ExternalLink className="size-3" />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-slate-500">{partner.description || partner.tavsif || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(partner)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedPartner(partner);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={partnersData?.meta?.pagination ?? partnersData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedPartner ? 'Hamkorni tahrirlash' : 'Yangi hamkor yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileUploadInput
                label="Logo (Rasm)"
                value={formData.logo}
                onChange={(url) => setFormData({ ...formData, logo: url })}
              />
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Veb-sayt URL</Label>
                <Input
                  placeholder="https://example.com"
                  value={formData.website || ''}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { name: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Hamkor nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.name || ''}
                          onChange={(e) => handleTranslationChange(locale, 'name', e.target.value)}
                          placeholder="Uzum Market"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="O'zbekistonning yetakchi e-commerce platformasi..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Hamkorni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu hamkor tizimdan butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default PartnersPage;
